import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { IEstatuto, useEstatuto } from '../../../../hooks/useEstatuto';
import { extractApiErrorMessage } from '../../../../utils/ErrorUtils';

/**
 * Correção de dado de uma pessoa pela equipe, depois do envio (decisão de 23/10). Pensado para a
 * lista grande: busca a pessoa, corrige e salva. Só o e-mail mudou: o back troca o convite na
 * Autentique (ninguém mais assina de novo). Outro dado: o back refaz só o documento afetado.
 */

type Campo = { nome: string; rotulo: string; sexo?: boolean };

const PESSOA: Campo[] = [
  { nome: 'nome', rotulo: 'Nome' },
  { nome: 'email', rotulo: 'E-mail' },
  { nome: 'cpf', rotulo: 'CPF' },
  { nome: 'sexo', rotulo: 'Sexo', sexo: true },
  { nome: 'rg_numero', rotulo: 'RG' },
  { nome: 'rg_orgao', rotulo: 'Órgão do RG' },
  { nome: 'nacionalidade', rotulo: 'Nacionalidade' },
  { nome: 'estado_civil', rotulo: 'Estado civil' },
  { nome: 'profissao', rotulo: 'Profissão' },
  { nome: 'endereco', rotulo: 'Endereço' },
];
const PRESENTE = PESSOA.filter(c => ['nome', 'email', 'cpf', 'sexo'].includes(c.nome));

/** Uma pessoa do formulário: onde ela mora no dado (`chave(campo)`) e os valores de hoje. */
type Pessoa = {
  rotulo: string;
  papel: string;
  campos: Campo[];
  chave: (c: string) => string;
  valor: Record<string, any>;
};

const pessoasDoFormulario = (estatuto: IEstatuto): Pessoa[] => {
  const dados: any = estatuto.dados_preenchidos ?? {};
  // Pelo formato do dado, como o back (a Baixa antiga foi preenchida com o formulário da constituição).
  if (!dados.convocacao) {
    return (dados.pessoas ?? []).map((p: any, i: number) => ({
      rotulo: p.nome,
      papel: p.cargo || (p.presente ? 'Fundador(a)' : 'Fundador(a) ausente'),
      campos: PESSOA,
      chave: (c: string) => `pessoas[${i}].${c}`,
      valor: p,
    }));
  }
  const a = dados.assembleia ?? {};
  const c = dados.convocacao ?? {};
  const prefixado = (prefixo: string, d: Record<string, any>) =>
    Object.fromEntries(
      Object.entries(d)
        .filter(([k]) => k.startsWith(`${prefixo}_`))
        .map(([k, v]) => [k.slice(prefixo.length + 1), v]),
    );
  // A mesma pessoa pode ser também fiel ou presidente: o back corrige todas as cópias juntas.
  const tambem = (cpf: string) =>
    [
      a.fiel_cpf === cpf && 'fiel depositário(a)',
      a.presidente_cpf === cpf && 'presidente (requerimento)',
    ].filter(Boolean);
  const lista: Pessoa[] = (a.presentes ?? []).map((p: any, i: number) => ({
    rotulo: p.nome,
    papel: ['Presente', ...tambem(p.cpf)].join(' · também '),
    campos: PRESENTE,
    chave: (campo: string) => `assembleia.presentes[${i}].${campo}`,
    valor: p,
  }));
  (
    [
      ['fiel', 'Fiel depositário(a)', PESSOA],
      ['presidente', 'Presidente (requerimento)', PESSOA],
    ] as const
  ).forEach(([prefixo, papel, campos]) => {
    if (a[`${prefixo}_nome`])
      lista.push({
        rotulo: a[`${prefixo}_nome`],
        papel,
        campos: [...campos],
        chave: campo => `assembleia.${prefixo}_${campo}`,
        valor: prefixado(prefixo, a),
      });
  });
  if (a.tesoureiro_nome)
    lista.push({
      rotulo: a.tesoureiro_nome,
      papel: 'Tesoureiro(a)',
      campos: PESSOA.filter(x => ['nome', 'sexo'].includes(x.nome)),
      chave: campo => `assembleia.tesoureiro_${campo}`,
      valor: prefixado('tesoureiro', a),
    });
  if (c.convocante_nome)
    lista.push({
      rotulo: c.convocante_nome,
      papel: 'Quem convoca (edital)',
      campos: PESSOA.filter(x => ['nome', 'email'].includes(x.nome)),
      chave: campo => `convocacao.convocante_${campo}`,
      valor: prefixado('convocante', c),
    });
  return lista;
};

export const EstatutoCorrigirPessoa: React.FC<{ estatuto: IEstatuto }> = ({ estatuto }) => {
  const { corrigirPessoa } = useEstatuto();
  const queryClient = useQueryClient();
  const [busca, setBusca] = useState('');
  const [aberta, setAberta] = useState<Pessoa | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const pessoas = useMemo(() => pessoasDoFormulario(estatuto), [estatuto]);

  const encontradas = useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (t.length < 2) return [];
    // CPF gravado só com dígitos: "123.456" acha também.
    const digitos = /^[\d.\-\s]+$/.test(t) ? t.replace(/\D/g, '') : '';
    return pessoas.filter(
      p =>
        `${p.rotulo} ${p.valor.email ?? ''}`.toLowerCase().includes(t) ||
        (!!digitos && String(p.valor.cpf ?? '').includes(digitos)),
    );
  }, [busca, pessoas]);
  const achadas = encontradas.slice(0, 8);

  const salvar = useMutation({
    mutationFn: (campos: Record<string, string>) => corrigirPessoa(estatuto.id, campos),
    onSuccess: res => {
      // Falha na Autentique ou documento já assinado: o aviso fica até a equipe fechar.
      if (res.data?.falhas?.length || res.data?.assinados?.length)
        toast.warning(res.message, { duration: Infinity });
      else toast.success(res.message || 'Dados corrigidos.');
      setAberta(null);
      setBusca('');
      queryClient.invalidateQueries({ queryKey: ['estatuto', estatuto.id] });
      queryClient.invalidateQueries({ queryKey: ['estatutos'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
      if (estatuto.kanban_card_id) {
        queryClient.invalidateQueries({ queryKey: ['autentique-status', estatuto.kanban_card_id] });
        queryClient.invalidateQueries({ queryKey: ['kanban-atividades', estatuto.kanban_card_id] });
      }
    },
    onError: (err: any) => toast.error(extractApiErrorMessage(err, 'Erro ao corrigir')),
  });

  const abrir = (p: Pessoa) => {
    setAberta(p);
    setForm(Object.fromEntries(p.campos.map(c => [c.nome, String(p.valor[c.nome] ?? '')])));
  };

  const enviar = () => {
    if (!aberta) return;
    const mudou = Object.fromEntries(
      aberta.campos
        .filter(c => form[c.nome] !== String(aberta.valor[c.nome] ?? ''))
        .map(c => [aberta.chave(c.nome), form[c.nome]]),
    );
    if (!Object.keys(mudou).length) {
      toast.info('Nada mudou.');
      return;
    }
    salvar.mutate(mudou);
  };

  // Formulário antigo (sem lista de pessoas): nada a corrigir por aqui.
  if (!pessoas.length) return null;

  return (
    <Box>
      <Text fontSize="sm" fontWeight="700" color="gray.700" mb={1}>
        Corrigir dados de uma pessoa
      </Text>
      <Text fontSize="11px" color="gray.500" mb={2}>
        Se só o e-mail mudar, o convite é trocado na Autentique e ninguém mais assina de novo. Outro
        dado refaz só o documento em que ele aparece.
      </Text>
      <Input
        size="sm"
        aria-label="Buscar pessoa para corrigir"
        placeholder="Buscar por nome, e-mail ou CPF"
        value={busca}
        onChange={e => setBusca(e.target.value)}
      />
      {achadas.length > 0 && (
        <Stack spacing={1} mt={2}>
          {achadas.map(p => (
            <Flex
              key={p.chave('nome')}
              justify="space-between"
              align="center"
              borderWidth="1px"
              borderRadius="md"
              px={2}
              py={1}
            >
              <Box>
                <Text fontSize="xs" fontWeight="600">
                  {p.rotulo}
                </Text>
                <Text fontSize="10px" color="gray.500">
                  {p.papel}
                  {p.valor.email ? ` · ${p.valor.email}` : ''}
                </Text>
              </Box>
              <Button size="xs" variant="outline" onClick={() => abrir(p)}>
                Corrigir
              </Button>
            </Flex>
          ))}
          {encontradas.length > achadas.length && (
            <Text fontSize="10px" color="gray.500">
              +{encontradas.length - achadas.length} — refine a busca
            </Text>
          )}
        </Stack>
      )}
      {busca.trim().length >= 2 && !encontradas.length && (
        <Text fontSize="xs" color="gray.500" mt={2}>
          Ninguém encontrado.
        </Text>
      )}

      <Modal isOpen={!!aberta} onClose={() => setAberta(null)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Corrigir {aberta?.rotulo}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              {aberta?.campos.map(c => (
                <FormControl key={c.nome}>
                  <FormLabel fontSize="xs" mb={1}>
                    {c.rotulo}
                  </FormLabel>
                  {c.sexo ? (
                    <Select
                      size="sm"
                      value={form[c.nome] ?? ''}
                      onChange={e => setForm({ ...form, [c.nome]: e.target.value })}
                    >
                      <option value="F">F</option>
                      <option value="M">M</option>
                    </Select>
                  ) : (
                    <Input
                      size="sm"
                      value={form[c.nome] ?? ''}
                      onChange={e => setForm({ ...form, [c.nome]: e.target.value })}
                    />
                  )}
                </FormControl>
              ))}
            </SimpleGrid>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={() => setAberta(null)}>
              Cancelar
            </Button>
            <Button size="sm" colorScheme="blue" onClick={enviar} isLoading={salvar.isPending}>
              Salvar correção
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
